/**
 * FC-QTE-02 — proposed open-ended development pricing for T4A, T4B, and T5.
 *
 * PROPOSED CALIBRATION. The wizard displays this development figure for T4A,
 * T4B, and T5. Panel and AI stay in calculateEstimate() so they are not
 * counted twice. Hosting stays a separate monthly rate.
 *
 * Classification stays in quoteEstimator.ts. This file does not retune tiers.
 */
import {
  ADVANCED_PANEL_MIN_MULTIPLIER,
  AI_SELF_HEALING_PRICE,
  CONTENT_PANEL_MULTIPLIER,
  DEVELOPMENT_TIERS,
  type CategoryCode,
  type Panel,
  type Scores,
} from './quoteEstimator';

const CATEGORIES: CategoryCode[] = ['A', 'B', 'C', 'D', 'E', 'F'];

/** Proposed reference profiles. Not classification thresholds. */
export const PROPOSED_REFERENCE_PROFILES = {
  T4A: { A: 1, B: 2, C: 4, D: 3, E: 1, F: 1 },
  T4B: { A: 1, B: 2, C: 4, D: 4, E: 2, F: 2 },
} as const satisfies Record<'T4A' | 'T4B', Scores>;

/** Proposed A–F weights for excess above the reference profile. */
export const PROPOSED_EXCESS_WEIGHTS = { A: 1, B: 1, C: 2, D: 2, E: 2, F: 2 } as const;

/** Proposed: Complexity Multiplier = 1 + (this × Weighted Excess). No cap. */
export const PROPOSED_EXCESS_RATE = 0.05;

/** Proposed scope-volume prices. Charged only above the included baseline. */
export const PROPOSED_SCOPE_ADDERS = {
  workflows: { included: 1, each: 1000 },
  externalIntegrations: { included: 1, each: 500 },
  persistentDomainEntities: { included: 3, each: 400 },
  userRoles: { included: 1, each: 600 },
  substantiveScreens: { included: 2, each: 350 },
} as const;

/** Proposed rounding step for T4A/T4B preliminary development. */
export const PROPOSED_ROUND_UP_TO = 250;

/**
 * Proposed resilience allowances. Not uptime promises.
 * Geographic, regulated, or unusual recovery has no published dollar figure.
 */
export const PROPOSED_RESILIENCE_FROM = {
  basic_failover: 2500,
  extensive_redundancy: 6000,
} as const;

export type ResilienceLevel = 'none' | 'basic_failover' | 'extensive_redundancy' | 'custom_review';

export type FReview =
  | 'normal'
  | 'elevated_security_note'
  | 'mandatory_security_review'
  | 'high_risk_acceptance_review';

export interface ScopeCounts {
  workflows?: number;
  externalIntegrations?: number;
  persistentDomainEntities?: number;
  userRoles?: number;
  substantiveScreens?: number;
}

interface CommonInput {
  scores: Scores;
  /** Omitted counts are the included baseline, so that row adds $0. */
  scope?: ScopeCounts;
  panel?: Panel;
  /** Used only for an advanced panel, and never below 1.00×. */
  advancedMultiplier?: number;
  aiSelfHealing?: boolean;
}

export interface T4QuoteInput extends CommonInput {
  tier: 'T4A' | 'T4B';
}

export interface T5QuoteInput extends CommonInput {
  tier: 'T5';
  /**
   * The application estimate resilience is added to once.
   * A full T4 price is not stacked on top of a full T5 price.
   */
  underlyingApplicationEstimate: number;
  resilience: ResilienceLevel;
}

export type OpenEndedQuoteInput = T4QuoteInput | T5QuoteInput;

export interface OpenEndedQuote {
  /** Always true: every dollar in this result is proposed calibration. */
  proposed: true;
  tier: 'T4A' | 'T4B' | 'T5';
  /**
   * Development commission before the management panel and AI.
   * Null when this module refuses to emit a figure.
   */
  development: number | null;
  /** False when the figure is withheld or is not a quotation that can be treated as final. */
  binding: boolean;
  weightedExcess: number;
  complexityMultiplier: number;
  scopeCost: number;
  panelCost: number;
  aiCost: number;
  /** development + panel + AI, or null when development is withheld. */
  total: number | null;
  fReview: FReview;
  /** Dollar allowance included once in a T5 development figure. Null when none is published. */
  resilienceAllowance: number | null;
  notes: string[];
}

export function weightedExcess(scores: Scores, reference: Scores): number {
  return CATEGORIES.reduce((sum, code) => {
    const over = Math.max(0, scores[code] - reference[code]);
    return sum + over * PROPOSED_EXCESS_WEIGHTS[code];
  }, 0);
}

export function complexityMultiplier(excess: number): number {
  return 1 + PROPOSED_EXCESS_RATE * excess;
}

/** Round up to the next step. Exact multiples stay put. */
export function roundUpToStep(amount: number, step: number): number {
  const units = amount / step;
  const nearest = Math.round(units);
  if (Math.abs(units - nearest) < 1e-6) return nearest * step;
  return Math.ceil(units) * step;
}

export function fReviewFor(score: Scores['F']): FReview {
  if (score <= 2) return 'normal';
  if (score === 3) return 'elevated_security_note';
  if (score === 4) return 'mandatory_security_review';
  return 'high_risk_acceptance_review';
}

function additional(count: number | undefined, included: number, each: number): number {
  if (count === undefined) return 0;
  return Math.max(0, count - included) * each;
}

/** Scope dollars above the included baseline. T4A never charges for persistent entities. */
export function scopeCostFor(tier: 'T4A' | 'T4B', scope: ScopeCounts | undefined): number {
  const rows = PROPOSED_SCOPE_ADDERS;
  const entities = tier === 'T4B'
    ? additional(scope?.persistentDomainEntities, rows.persistentDomainEntities.included, rows.persistentDomainEntities.each)
    : 0;
  return (
    additional(scope?.workflows, rows.workflows.included, rows.workflows.each)
    + additional(scope?.externalIntegrations, rows.externalIntegrations.included, rows.externalIntegrations.each)
    + entities
    + additional(scope?.userRoles, rows.userRoles.included, rows.userRoles.each)
    + additional(scope?.substantiveScreens, rows.substantiveScreens.included, rows.substantiveScreens.each)
  );
}

function panelCostFor(development: number, panel: Panel, advancedMultiplier: number): number {
  if (panel === 'content') return development * CONTENT_PANEL_MULTIPLIER;
  if (panel === 'advanced') {
    return development * Math.max(ADVANCED_PANEL_MIN_MULTIPLIER, advancedMultiplier);
  }
  return 0;
}

/** E 0–2 adds nothing. E 3 and E 4 use the proposed allowances. E 5 invents no geographic surcharge. */
export function resilienceAllowanceForE(infrastructureScore: number): number {
  if (infrastructureScore === 3) return PROPOSED_RESILIENCE_FROM.basic_failover;
  if (infrastructureScore === 4) return PROPOSED_RESILIENCE_FROM.extensive_redundancy;
  return 0;
}

/**
 * Preliminary development price shown on the calculator.
 * Scope counts are omitted, so only A–F excess above the reference moves the figure.
 * Never below the approved floor. Always a number, including F4/F5.
 */
export function displayedOpenEndedDevelopment(
  tier: 'T4A' | 'T4B' | 'T5',
  scores: Scores,
  persistentData: boolean,
): { base: number; floor: number } {
  if (tier === 'T4A' || tier === 'T4B') {
    return {
      base: t4Development(tier, scores, undefined).development,
      floor: DEVELOPMENT_TIERS[tier].min,
    };
  }
  const underlyingTier = persistentData ? 'T4B' : 'T4A';
  const underlying = t4Development(underlyingTier, scores, undefined).development;
  const floor = DEVELOPMENT_TIERS.T5.min;
  return {
    base: Math.max(floor, underlying + resilienceAllowanceForE(scores.E)),
    floor,
  };
}

function t4Development(tier: 'T4A' | 'T4B', scores: Scores, scope: ScopeCounts | undefined) {
  const excess = weightedExcess(scores, PROPOSED_REFERENCE_PROFILES[tier]);
  const multiplier = complexityMultiplier(excess);
  const scopeCost = scopeCostFor(tier, scope);
  const floor = DEVELOPMENT_TIERS[tier].min;
  const raw = (floor + scopeCost) * multiplier;
  const development = Math.max(floor, roundUpToStep(raw, PROPOSED_ROUND_UP_TO));
  return { excess, multiplier, scopeCost, development };
}

function resilienceDollars(level: ResilienceLevel): number | null {
  if (level === 'none') return 0;
  if (level === 'custom_review') return null;
  return PROPOSED_RESILIENCE_FROM[level];
}

/**
 * Proposed T4A/T4B/T5 figure. Does not select a tier and does not price hosting.
 */
export function quoteOpenEnded(input: OpenEndedQuoteInput): OpenEndedQuote {
  const panel = input.panel ?? 'none';
  const advancedMultiplier = input.advancedMultiplier ?? ADVANCED_PANEL_MIN_MULTIPLIER;
  const review = fReviewFor(input.scores.F);
  const notes = ['Proposed calibration — not approved policy. Hosting remains a separate base rate.'];

  if (review === 'elevated_security_note') {
    notes.push('F3: elevated security note. No hosting surcharge is added.');
  } else if (review === 'mandatory_security_review') {
    notes.push('F4: mandatory security review before a final quotation.');
  } else if (review === 'high_risk_acceptance_review') {
    notes.push('F5: high-risk acceptance review. A binding T5 price is not emitted.');
  }

  let development: number | null;
  let excess = 0;
  let multiplier = 1;
  let scopeCost = 0;
  let resilienceAllowance: number | null = null;

  if (input.tier === 'T5') {
    const allowance = resilienceDollars(input.resilience);
    resilienceAllowance = allowance;
    if (allowance === null) {
      development = null;
      notes.push('Geographic, regulated, or unusual recovery is a custom review. No resilience dollar figure is published.');
    } else if (review === 'high_risk_acceptance_review') {
      development = null;
    } else {
      const combined = input.underlyingApplicationEstimate + allowance;
      development = Math.max(DEVELOPMENT_TIERS.T5.min, combined);
    }
  } else {
    const priced = t4Development(input.tier, input.scores, input.scope);
    excess = priced.excess;
    multiplier = priced.multiplier;
    scopeCost = priced.scopeCost;
    development = priced.development;
  }

  const pricedDevelopment = development;
  const binding = pricedDevelopment !== null && review !== 'mandatory_security_review' && review !== 'high_risk_acceptance_review';
  const panelCost = pricedDevelopment === null ? 0 : panelCostFor(pricedDevelopment, panel, advancedMultiplier);
  const aiCost = pricedDevelopment !== null && input.aiSelfHealing ? AI_SELF_HEALING_PRICE : 0;

  return {
    proposed: true,
    tier: input.tier,
    development: pricedDevelopment,
    binding,
    weightedExcess: excess,
    complexityMultiplier: multiplier,
    scopeCost,
    panelCost,
    aiCost,
    total: pricedDevelopment === null ? null : pricedDevelopment + panelCost + aiCost,
    fReview: review,
    resilienceAllowance,
    notes,
  };
}
